import { useEffect, useMemo, useState } from "react";
import DataTable from "react-data-table-component";
import api from "../services/api";

const defaultForm = {
  promptName: "",
  prompt: "",
  enabled: true
};

const truncateText = (text = "", length = 80) => {
  if (text.length <= length) return text;
  return `${text.slice(0, length)}...`;
};

export default function SystemPrompt() {
  const [prompts, setPrompts] = useState([]);
  const [search, setSearch] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(defaultForm);

  useEffect(() => {
    const fetchSystemPrompts = async () => {
      setLoading(true);
      try {
        const res = await api.get(
          "https://aidemo.fusionsystemsinc.com/api/v1/system_prompt/list",
          {
            headers: {
              accept: "application/json"
            }
          }
        );

        const items = Array.isArray(res.data?.items) ? res.data.items : [];
        setPrompts(
          items.map(item => ({
            id: item.id,
            promptName: item.name || "",
            prompt: item.prompt || "",
            enabled: item.is_active === 1
          }))
        );
      } catch (err) {
        console.error("Failed to fetch system prompts", err);
      } finally {
        setLoading(false);
      }
    };

    fetchSystemPrompts();
  }, []);

  const filteredPrompts = useMemo(() => {
    return prompts.filter(item =>
      item.promptName.toLowerCase().includes(search.toLowerCase())
    );
  }, [prompts, search]);

  const onAdd = () => {
    setIsEdit(false);
    setEditId(null);
    setForm(defaultForm);
    setOpenModal(true);
  };

  const onEdit = row => {
    setIsEdit(true);
    setEditId(row.id);
    setForm({
      promptName: row.promptName,
      prompt: row.prompt,
      enabled: row.enabled
    });
    setOpenModal(true);
  };

  const closeModal = () => {
    setOpenModal(false);
    setForm(defaultForm);
    setIsEdit(false);
    setEditId(null);
  };

  const saveSystemPrompt = async () => {
    if (!form.promptName.trim() || !form.prompt.trim()) {
      alert("Prompt Name and Prompt are required.");
      return;
    }

    setSaving(true);

    try {
      const payload = {
        name: form.promptName.trim(),
        prompt: form.prompt.trim(),
        is_active: form.enabled ? 1 : 0
      };

      if (isEdit && editId) {
        payload.id = editId;
      }

      const res = await api.post(
        "https://aidemo.fusionsystemsinc.com/api/v1/system_prompt/save",
        payload,
        {
          headers: {
            accept: "application/json"
          }
        }
      );

      const savedItem = res.data?.item;

      if (savedItem) {
        const mappedSavedItem = {
          id: savedItem.id,
          promptName: savedItem.name || "",
          prompt: savedItem.prompt || "",
          enabled: savedItem.is_active === 1
        };

        if (isEdit) {
          setPrompts(prev =>
            prev.map(item => (item.id === editId ? mappedSavedItem : item))
          );
        } else {
          setPrompts(prev => [mappedSavedItem, ...prev]);
        }
      } else if (isEdit) {
        setPrompts(prev =>
          prev.map(item =>
            item.id === editId
              ? {
                  ...item,
                  promptName: form.promptName.trim(),
                  prompt: form.prompt.trim(),
                  enabled: form.enabled
                }
              : item
          )
        );
      } else {
        setPrompts(prev => [
          {
            id: Date.now(),
            promptName: form.promptName.trim(),
            prompt: form.prompt.trim(),
            enabled: form.enabled
          },
          ...prev
        ]);
      }
    } catch (err) {
      console.error("System prompt save failed", err);
      alert("Failed to save system prompt.");
    } finally {
      setSaving(false);
      closeModal();
    }
  };

  const onDelete = async row => {
    const shouldDelete = window.confirm(
      `Are you sure you want to delete "${row.promptName}"?`
    );
    if (!shouldDelete) return;

    try {
      const res = await api.post(
        "https://aidemo.fusionsystemsinc.com/api/v1/system_prompt/delete",
        {},
        {
          params: { id: row.id },
          headers: {
            accept: "application/json"
          }
        }
      );

      if (res.data?.status === "DELETED" && res.data?.item?.is_delete === 1) {
        setPrompts(prev => prev.filter(item => item.id !== row.id));
      } else {
        alert("Delete request completed, but response was unexpected.");
      }
    } catch (err) {
      console.error("System prompt delete failed", err);
      alert("Failed to delete system prompt.");
    }
  };

  const columns = [
    {
      name: "Prompt Name",
      selector: row => row.promptName,
      sortable: true,
      grow: 3,
      wrap: true,
      center: true
    },
    {
      name: "Prompt",
      sortable: true,
      grow: 5,
      cell: row => (
        <span title={row.prompt} style={{ cursor: "help" }}>
          {truncateText(row.prompt, 90)}
        </span>
      ),
      center: true,
      wrap: true
    },
    {
      name: "Active",
      selector: row => (row.enabled ? "Yes" : "No"),
      sortable: true,
      grow: 1,
      center: true,
      wrap: true
    },
    {
      name: "Action",
      center: true,
      grow: 2,
      cell: row => (
        <div className="admin-actions">
          <button
            className="admin-btn primary icon"
            onClick={() => onEdit(row)}
            title="Edit prompt"
          >
            <i className="fa-solid fa-pencil"></i>
          </button>
          <button
            className="admin-btn danger icon"
            onClick={() => onDelete(row)}
            title="Delete prompt"
          >
            <i className="fa-solid fa-trash"></i>
          </button>
        </div>
      )
    }
  ];

  const loaderComponent = (
    <div className="system-prompt-loader" role="status" aria-live="polite">
      <span className="system-prompt-loader-spinner" />
      <span className="system-prompt-loader-text">Loading prompts...</span>
    </div>
  );

  return (
    <div className="admin-page">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16
        }}
      >
        <h2 style={{ marginBottom: 0 }}>System Prompts</h2>
        <button className="admin-btn primary" onClick={onAdd}>
          + Add
        </button>
      </div>

      <DataTable
        columns={columns}
        data={filteredPrompts}
        progressPending={loading}
        progressComponent={loaderComponent}
        pagination
        fixedHeader
        fixedHeaderScrollHeight="65vh"
        highlightOnHover
        responsive
        subHeader
        subHeaderComponent={
          <input
            type="text"
            placeholder="Search prompt..."
            className="datatable-search"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        }
      />

      {openModal && (
        <div className="admin-modal-backdrop">
          <div className="admin-modal system-prompt-modal">
            <h3 style={{ marginTop: 0 }}>
              {isEdit ? "Edit System Prompt" : "Add System Prompt"}
            </h3>

            <div style={{ marginBottom: 12 }}>
              <label
                htmlFor="prompt-name"
                style={{ display: "block", marginBottom: 6, fontWeight: 600 }}
              >
                Prompt Name
              </label>
              <input
                id="prompt-name"
                type="text"
                value={form.promptName}
                onChange={e =>
                  setForm(prev => ({
                    ...prev,
                    promptName: e.target.value
                  }))
                }
                style={{
                  width: "97%",
                  padding: "8px 10px",
                  borderRadius: 6,
                  border: "1px solid #d1d5db"
                }}
              />
            </div>

            <div style={{ marginBottom: 12 }}>
              <label
                htmlFor="prompt-body"
                style={{ display: "block", marginBottom: 6, fontWeight: 600 }}
              >
                Prompt
              </label>
              <textarea
                id="prompt-body"
                value={form.prompt}
                onChange={e =>
                  setForm(prev => ({
                    ...prev,
                    prompt: e.target.value
                  }))
                }
                rows={16}
                style={{
                  width: "97%",
                  padding: "8px 10px",
                  borderRadius: 6,
                  border: "1px solid #d1d5db",
                  resize: "vertical"
                }}
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label
                htmlFor="prompt-enabled"
                style={{
                  display: "inline-flex",
                  gap: 8,
                  alignItems: "center",
                  cursor: "pointer",
                  fontWeight: 600
                }}
              >
                <input
                  id="prompt-enabled"
                  type="checkbox"
                  checked={form.enabled}
                  onChange={e =>
                    setForm(prev => ({
                      ...prev,
                      enabled: e.target.checked
                    }))
                  }
                />
                Enable
              </label>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button
                className="admin-btn secondary"
                onClick={closeModal}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                className="admin-btn primary"
                onClick={saveSystemPrompt}
                disabled={saving}
              >
                {saving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
